import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { getStoredAuth, setStoredAuth } from '@/lib/auth-session';
import { DeleteAccountButton } from './DeleteAccountButton';
import { removeCookie } from '@/utils';

function InfoCard({ label, value }) {
  return (
    <div className="card border-secondary bg-light">
      <div className="card-body">
        <dt className="text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.1em' }}>
          {label}
        </dt>
        <dd className="text-dark mb-0" style={{ fontSize: '0.875rem', wordBreak: 'break-word' }}>
          {value || "Unavailable"}
        </dd>
      </div>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [auth, setAuth] = useState(() => getStoredAuth());
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const fullName = [auth?.given_name, auth?.family_name]
    .filter(Boolean)
    .join(" ")
    .trim();

  async function handleLogout() {
    if (!window.confirm("Are you sure you want to sign out?")) {
      return;
    }

    setIsLoggingOut(true);

    try {
      await signOut();
    } catch {
      setIsLoggingOut(false);
      window.alert("Unable to sign out right now. Please try again.");
    }
  }

  useEffect(() => {
    // No need for this since we're here
    removeCookie('DEFAULT_URL');
    fetchAuthSession()
      .then((user) => {
        const idToken = user.tokens?.idToken;
        const accessToken = user.tokens?.accessToken;

        if (!idToken || !accessToken) {
          navigate("/");
          return null;
        }

        const nextAuth = {
          family_name: idToken.payload.family_name?.toString(),
          given_name: idToken.payload.given_name?.toString(),
          email: idToken.payload.email?.toString(),
          username: accessToken.payload.username?.toString(),
          groups: Array.isArray(idToken.payload["cognito:groups"])
            ? idToken.payload["cognito:groups"].map((group) => (group || "").toString())
            : undefined,
          idToken: idToken.toString(),
        };

        setStoredAuth(nextAuth);
        setAuth(nextAuth);
        return nextAuth;
      })
      .catch(() => {
        navigate("/");
      });
  }, [navigate]);

  if (!auth) {
    return (
      <main className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <p className="text-muted">Checking your session...</p>
      </main>
    );
  }

  return (
    <main className="min-vh-100 bg-light py-4 px-3">
      <div className="container">
        <div className="card shadow">
          <div className="row g-0">
            {/* Left panel - primary info */}
            <div className="col-lg-5 text-white p-4" style={{ background: 'linear-gradient(160deg, #423894 0%, #4a40a2 52%, #6f7ce8 100%)' }}>
              <p className="text-uppercase mb-0" style={{ fontSize: '0.7rem', letterSpacing: '0.15em', opacity: 0.7 }}>
                OpenPath AI
              </p>
              <h1 className="mb-3" style={{ fontSize: '2.5rem', fontWeight: 600, letterSpacing: '-0.03em' }}>
                Welcome back
              </h1>
              <p className="mb-0" style={{ opacity: 0.78, lineHeight: 1.6 }}>
                Your Google account is connected and your session is active for
                this browser tab.
              </p>

              <div className="mt-4 p-3 rounded" style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)' }}>
                <p className="text-uppercase mb-1" style={{ fontSize: '0.65rem', letterSpacing: '0.12em', opacity: 0.6 }}>
                  Signed in as
                </p>
                <p className="mb-1" style={{ fontSize: '1.5rem', fontWeight: 600 }}>
                  {fullName || "OpenPath AI User"}
                </p>
                <p className="mb-0" style={{ fontSize: '0.9rem', opacity: 0.76, wordBreak: 'break-all' }}>
                  {auth.email || "Email unavailable"}
                </p>
              </div>

              <button
                type="button"
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="btn btn-outline-light mt-3"
              >
                {isLoggingOut ? "Signing out..." : "Sign out"}
              </button>
            </div>

            {/* Right panel - account management */}
            <div className="col-lg-7 bg-white p-4">
              <div className="card border-primary bg-light mb-3">
                <div className="card-body">
                  <p className="text-uppercase mb-1" style={{ fontSize: '0.65rem', letterSpacing: '0.12em', color: '#ff59d6' }}>
                    Account Data
                  </p>
                  <h2 className="mb-2" style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                    Delete account request
                  </h2>
                  <p className="text-muted mb-3" style={{ fontSize: '0.875rem', lineHeight: 1.6 }}>
                    If you choose to delete your account, your user data will be
                    deleted permanently.
                  </p>

                  <div className="alert alert-danger mb-3" role="alert">
                    <p className="mb-0" style={{ fontSize: '0.875rem' }}>
                      Deleting your account will permanently remove your profile and
                      associated data from both the OpenPath AI web app and mobile
                      app. This action cannot be undone.
                    </p>
                  </div>

                  <DeleteAccountButton
                    className="btn btn-danger"
                    label="Delete My Account"
                  />
                </div>
              </div>

              <div className="row g-3">
                <div className="col-6">
                  <InfoCard label="Full name" value={fullName} />
                </div>
                <div className="col-6">
                  <InfoCard label="Email" value={auth.email} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}